// ============================================================================
// TeamPlanningView (L05) — bascule « Grille ↔ Graphique » autour du board
// ============================================================================
// Port FIDELE du PoC components/salary/team-planning-view.tsx (85 L @ SHA
// 7e40e4a). Encadre la grille (DirectorGridBoard, SL-B) d'une bascule. Mode
// graphique = frise de la MASSE SALARIALE AGREGEE de l'equipe (M-657 : totaux
// annuels SEULS, calcules server-side ; aucun salaire individuel) — meme rendu que
// SalaryProgressionChart individuel (deja porte, ManagementTab).
//
// TRANSPOSITION (§SL-C) : Segmented @/components/ui → GsSegmented @gs/ui (motif
// page/board) ; Card @/components/ui → Card ../employee/ui ; EmptyState → @gs/ui ;
// libelles FR → i18n FR+EN (P13). Regle #4 zero emoji ; Regle #28 dark-aware.

import { useState, type ComponentProps } from 'react';
import { GsSegmented, EmptyState } from '@gs/ui';
import { LineChart } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { Card } from '../employee/ui';
import { SalaryProgressionChart } from '../employee/SalaryProgressionChart';
import type { SalaryTimeline } from '../../lib/salary/progression';
import { DirectorGridBoard } from './DirectorGridBoard';

type Mode = 'grille' | 'graphique';

/**
 * Vue de planification du directeur — la timeline agregee est calculee server-side
 * (ce composant ne reçoit que des totaux annuels, aucun salaire individuel, M-657).
 */
export function TeamPlanningView({
  teamTimeline,
  ...gridBoardProps
}: { teamTimeline: SalaryTimeline } & ComponentProps<typeof DirectorGridBoard>) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('grille');

  return (
    <div className="flex flex-col gap-6">
      <GsSegmented
        value={mode}
        onChange={(v) => setMode(v as Mode)}
        ariaLabel={t('gest:planification.teamView.modeSelector')}
        options={[
          { value: 'grille', label: t('gest:planification.teamView.tabGrid') },
          { value: 'graphique', label: t('gest:planification.teamView.tabChart') },
        ]}
      />

      {mode === 'grille' ? (
        <DirectorGridBoard {...gridBoardProps} />
      ) : (
        <TeamSalaryChartCard timeline={teamTimeline} />
      )}
    </div>
  );
}

function TeamSalaryChartCard({ timeline }: { timeline: SalaryTimeline }) {
  const { t } = useTranslation();
  const hasData = timeline.points.some((p) => p.annual !== null);

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-chip font-medium uppercase tracking-wide text-slate-400">
            {t('gest:planification.teamView.chartEyebrow')}
          </span>
          <p className="text-field text-slate-500 dark:text-slate-400">
            {t('gest:planification.teamView.chartDescription')}
          </p>
        </div>

        {hasData ? (
          <>
            <SalaryProgressionChart timeline={timeline} bonusByYear={new Map()} avantages={null} />
            <p className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:planification.teamView.chartFootnote')}
            </p>
          </>
        ) : (
          <EmptyState
            icon={<LineChart size={40} aria-hidden="true" />}
            title={t('gest:planification.teamView.emptyTitle')}
            description={t('gest:planification.teamView.emptyDescription')}
          />
        )}
      </div>
    </Card>
  );
}
